import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { FcpTreeItemMapper } from '../../domain/mappers/fcpTreeItem.mapper';
import { FcpTreeItemType } from '../../domain/models/fcp.model';
import { FcpTreeItem } from '../../domain/models/fcpTreeItem.model';

export interface FcpTreeRenderItem extends FcpTreeItem {
    adjustedValue: number; // closing value of the item
}
export interface Total {
    adjustedValue: number; // closing value of the item
    currentYear: number; // closing value of the item
    previousYear: number; // closing value of the item
}
@UntilDestroy()
@Component({
    selector: 'aumico-fcp-tree',
    templateUrl: 'fcpTree.component.html',
    styleUrls: ['fcpTree.component.scss'],
})
export class FcpTreeComponent implements OnInit, OnChanges {
    FcpTreeItemType = FcpTreeItemType; // Make enum accessible in template
    @Input() treeRoot: FcpTreeItem;
    @Input() showAdjustments: boolean = false;
    @Input() outputFinStatements: IFinStatements[];
    @Input() inputFinStatements: IFinStatements[];
    @Input() filter: string = '';
    @Input() dueDateCY: number | undefined;
    @Input() dueDatePY: number | undefined;

    private modelSubject = new BehaviorSubject<FcpTreeItem>(null);
    subNavigationItems$: Observable<FcpTreeItem[]>;
    activeSubNavigationItem: string;
    subNavigationItemTotals: Total[];

    constructor() {}
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['treeRoot']) {
            const previousValue = changes['treeRoot'].previousValue;
            const currentValue = changes['treeRoot'].currentValue;

            if (!isEqual(previousValue, currentValue)) {
                this.modelSubject.next(currentValue);
            }
        }
        if (changes['outputFinStatements']) {
            console.log('outputFinStatements changed');
            this.calculateSubNavigationItemTotals();
        }
    }

    ngOnInit() {
        this.initializeForm();

        this.subNavigationItems$ = this.modelSubject.pipe(
            map((domainModel) => this.getSubNavigationItems(domainModel)),
        );
    }

    private initializeForm() {}

    private getSubNavigationItems(model: FcpTreeItem): FcpTreeItem[] {
        if (!model || !model.children) {
            return [];
        }
        const valueToReturn = FcpTreeItemMapper.filterItemsByType(
            FcpTreeItemMapper.flattenTree(model.children),
            FcpTreeItemType.SubNavigation,
        );
        this.activeSubNavigationItem = model.name;
        this.calculateSubNavigationItemTotals();
        return valueToReturn;
    }

    private calculateSubNavigationItemTotals(): void {
        if (this.outputFinStatements && this.outputFinStatements.length > 0) {
            if (this.activeSubNavigationItem === 'Balance') {
                //Handle Activa and Passiva
                const activa = this.outputFinStatements[0].templateAndAccountsMapping.template.group.groups[0];
                const passiva = this.outputFinStatements[0].templateAndAccountsMapping.template.group.groups[1];
                const activaInput = this.inputFinStatements[0].templateAndAccountsMapping.template.group.groups[0];
                const passivaInput = this.inputFinStatements[0].templateAndAccountsMapping.template.group.groups[1];
                const totalActiva: Total = {
                    adjustedValue: this.calculateAdjustedValues(activa.name, this.outputFinStatements),
                    currentYear: this.calculateAdjustedValues(activaInput.name, this.inputFinStatements),
                    previousYear: activa.calculatedOpeningBalance,
                };
                const totalPassiva: Total = {
                    adjustedValue: this.calculateAdjustedValues(passiva.name, this.outputFinStatements),
                    currentYear: this.calculateAdjustedValues(passivaInput.name, this.inputFinStatements),
                    previousYear: passiva.calculatedOpeningBalance,
                };
                this.subNavigationItemTotals = [totalActiva, totalPassiva];
            } else if (this.activeSubNavigationItem === 'Income') {
                //Handle Income
                const income = this.outputFinStatements[1].templateAndAccountsMapping.template.group.groups[0];
                const incomeInput = this.inputFinStatements[1].templateAndAccountsMapping.template.group.groups[0];
                const totalIncome: Total = {
                    adjustedValue: this.calculateAdjustedValues(income.name, this.outputFinStatements),
                    currentYear: this.calculateAdjustedValues(incomeInput.name, this.inputFinStatements),
                    previousYear: income.calculatedOpeningBalance,
                };
                this.subNavigationItemTotals = [totalIncome];
            }
        }
    }

    protected calculateAdjustedValues(name: string, finStatements: IFinStatements[]): number {
        const outPutSortedItem = FcpTreeItemMapper.findSortedItemByName(finStatements, name);
        if (outPutSortedItem == null) {
            return 0;
        } else {
            return FcpTreeItemMapper.sumAccounts(outPutSortedItem).totalClosingBalance;
        }
    }
}
